import React, { useState, useEffect } from "react";
import FormItem from "../../components/ui/FormItem";
import FormGrid from "../../components/ui/FormGrid";
import FormActions from "../ui/FormActions";
import { DEFAULT_CHATBOX } from "../../data/intialData";
import FileUpload from "../common/fileupload/FileUpload";

export default function ChatBoxConfigurationForm({
  chatboxSettings,
  onSubmit,
  isLoading,
}) {
  const [formData, setFormData] = useState({
    greeting: DEFAULT_CHATBOX.greeting,
    primary_color: DEFAULT_CHATBOX.primary_color,
    secondary_color: DEFAULT_CHATBOX.secondary_color,
    position: DEFAULT_CHATBOX.position,
    x_axis: DEFAULT_CHATBOX.x_axis,
    y_axis: DEFAULT_CHATBOX.y_axis,
    logo: null,
  });

  const [errors, setErrors] = useState({});

  // ✅ Initialize form
  useEffect(() => {
    if (!chatboxSettings) return;

    setFormData({
      greeting:
        chatboxSettings?.greeting || DEFAULT_CHATBOX.greeting,
      primary_color:
        chatboxSettings?.primary_color ||
        DEFAULT_CHATBOX.primary_color,
      secondary_color:
        chatboxSettings?.secondary_color ||
        DEFAULT_CHATBOX.secondary_color,
      position:
        chatboxSettings?.position || DEFAULT_CHATBOX.position,
      x_axis:
        chatboxSettings?.x_axis || DEFAULT_CHATBOX.x_axis,
      y_axis:
        chatboxSettings?.y_axis || DEFAULT_CHATBOX.y_axis,
      logo: chatboxSettings?.logo_url || null, // always reset file
    });
  }, [chatboxSettings]);

  // ✅ Normal field change
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // ✅ File change (separate)
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({
        ...prev,
        logo: "Only image files allowed",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      logo: file,
    }));

    setErrors((prev) => ({
      ...prev,
      logo: null,
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.greeting)
      newErrors.greeting = "Greeting required";

    if (!formData.primary_color)
      newErrors.primary_color = "Primary color required";

    if (!formData.secondary_color)
      newErrors.secondary_color = "Secondary color required";

    if (!formData.position)
      newErrors.position = "Position required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Build FormData
  const buildPayload = () => {
    const payload = new FormData();

    payload.append("greeting", formData.greeting);
    payload.append("primary_color", formData.primary_color);
    payload.append("secondary_color", formData.secondary_color);
    payload.append("position", formData.position);
    payload.append("x_axis", parseInt(formData.x_axis));
    payload.append("y_axis", parseInt(formData.y_axis));

    if (formData.logo instanceof File) {
      payload.append("logo", formData.logo);
    }

    return payload;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onSubmit(buildPayload());
  };

  const handleCancel = () => {
    if (!chatboxSettings) return;

    setFormData({
      greeting:
        chatboxSettings?.greeting || DEFAULT_CHATBOX.greeting,
      primary_color:
        chatboxSettings?.primary_color ||
        DEFAULT_CHATBOX.primary_color,
      secondary_color:
        chatboxSettings?.secondary_color ||
        DEFAULT_CHATBOX.secondary_color,
      position:
        chatboxSettings?.position || DEFAULT_CHATBOX.position,
      x_axis:
        chatboxSettings?.x_axis || DEFAULT_CHATBOX.x_axis,
      y_axis:
        chatboxSettings?.y_axis || DEFAULT_CHATBOX.y_axis,
      logo: null,
    });

    setErrors({});
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormGrid>
        <FormItem
          label="Greeting Message"
          name="greeting"
          value={formData.greeting}
          onChange={handleChange}
          error={errors.greeting}
          required
        />

        <FormItem
          label="Primary Color"
          name="primary_color"
          type="color"
          value={formData.primary_color}
          onChange={handleChange}
          error={errors.primary_color}
          required
        />

        <FormItem
          label="Secondary Color"
          name="secondary_color"
          type="color"
          value={formData.secondary_color}
          onChange={handleChange}
          error={errors.secondary_color}
          required
        />

        <FormItem label="Chat Position" error={errors.position} required>
          <select
            name="position"
            value={formData.position}
            onChange={handleChange}
            className="form-input"
          >
            <option value="left">Left</option>
            <option value="right">Right</option>
          </select>
        </FormItem>

        <FormItem label="X-Axis" name="x_axis" type="number" value={formData.x_axis} onChange={handleChange} error={errors.x_axis} required />
        <FormItem label="Y-Axis" name="y_axis" type="number" value={formData.y_axis} onChange={handleChange} error={errors.y_axis} required />
      </FormGrid>
          <FormItem label="Logo" error={errors.logo}>
          <FileUpload
            name="logo"
            value={formData.logo}
            onChange={handleFileChange}
            error={errors.logo}
            accept="image/*"
          />
        </FormItem>

      <FormActions
        isLoading={isLoading}
        onCancel={handleCancel}
        saveText="Save Chat Box"
      />
    </form>
  );
}